import type { RoleFitTopology } from '@core/types';
import type { OptimizationResult } from '@modules/single-optimize/aiResumeOptimizer/shared/types';
import type { GapAnalysis } from '@soVendor/utils/jdAnalysis';
import type { RoleFitRow } from './computePairFit';

/** Heuristic "after optimization" band (B2C-style trajectory), clamped 0–100. */
export function projectedMatchRange(atsOverall: number): { min: number; max: number } {
  const headroom = 100 - atsOverall;
  const boostLo = Math.min(35, Math.max(8, Math.round(headroom * 0.35)));
  const boostHi = Math.min(45, boostLo + 10);
  return {
    min: Math.min(100, atsOverall + boostLo),
    max: Math.min(100, atsOverall + boostHi),
  };
}

function skillItemsToSkills(items: { skill?: string }[]): string[] {
  return items.map((i) => i.skill).filter(Boolean) as string[];
}

export function roleFitRowToAnalysisJson(row: RoleFitRow): Record<string, unknown> {
  return {
    matchedSkills: [
      ...skillItemsToSkills(row.gap.strongMatches),
      ...skillItemsToSkills(row.gap.weakMatches),
      ...skillItemsToSkills(row.gap.impliedMatches),
    ],
    missingSkills: skillItemsToSkills(row.gap.missingSkills),
    impliedSkills: skillItemsToSkills(row.gap.impliedMatches),
    jdCoverage: row.coverageScore,
    atsSections: row.atsSections,
    atsRating: row.atsRating,
  };
}

export function buildRolefitProjectionUpsertPayload(
  orgId: string,
  topology: RoleFitTopology,
  row: RoleFitRow,
  createdBy: string | null
): Record<string, unknown> {
  const { min, max } = projectedMatchRange(row.atsOverall);
  return {
    org_id: orgId,
    candidate_id: row.candidate.id,
    job_id: row.job.id,
    topology,
    fitment_score: Math.round(row.fitmentScore),
    coverage_score: Math.round(row.coverageScore),
    ats_overall: Math.round(row.atsOverall),
    ats_rating: row.atsRating,
    projected_match_min: min,
    projected_match_max: max,
    match_probability: Math.round((row.fitmentScore / 100) * 10000) / 10000,
    analysis_json: roleFitRowToAnalysisJson(row),
    created_by: createdBy,
  };
}

function atsBandFromScore(score: number): RoleFitRow['atsRating'] {
  if (score >= 85) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 50) return 'Fair';
  return 'Poor';
}

function gapAnalysisToAnalysisFields(gap: GapAnalysis | undefined): Record<string, unknown> {
  if (!gap) return {};
  const pick = (items: { skill?: string }[]) => items.map((i) => i.skill).filter(Boolean);
  return {
    matchedSkills: [...pick(gap.strongMatches), ...pick(gap.weakMatches), ...pick(gap.impliedMatches)],
    missingSkills: pick(gap.missingSkills),
    impliedSkills: pick(gap.impliedMatches),
    jdCoverage: gap.coverageScore,
  };
}

/**
 * After Single Optimize (RoleFit path), upsert the saved projection row so RoleFit AI “Saved projections”
 * and pair detail reflect post-LLM scores and link to the B2C optimization record.
 */
export function buildRolefitProjectionUpsertFromOptimization(
  orgId: string,
  topology: RoleFitTopology,
  candidateId: string,
  jobId: string,
  result: OptimizationResult,
  b2cOptimizationId: string | null,
  createdBy: string | null
): Record<string, unknown> {
  const afterOverall = Math.round(
    result.afterScores?.overall ?? result.optimizedScore ?? result.beforeScores?.overall ?? 0
  );
  const coverage = result.gapAnalysis?.coverageScore != null
    ? Math.round(result.gapAnalysis.coverageScore)
    : afterOverall;
  const fitment = Math.min(
    100,
    Math.max(0, Math.round(result.optimizedScore ?? afterOverall))
  );
  const { min, max } = projectedMatchRange(afterOverall);
  return {
    org_id: orgId,
    candidate_id: candidateId,
    job_id: jobId,
    topology,
    fitment_score: fitment,
    coverage_score: Math.min(100, Math.max(0, coverage)),
    ats_overall: Math.min(100, Math.max(0, afterOverall)),
    ats_rating: atsBandFromScore(afterOverall),
    projected_match_min: min,
    projected_match_max: max,
    match_probability: Math.round((fitment / 100) * 10000) / 10000,
    analysis_json: {
      ...gapAnalysisToAnalysisFields(result.gapAnalysis),
      b2c_optimization_id: b2cOptimizationId,
      optimized_at: new Date().toISOString(),
      source: 'single_optimize_rolefit',
      score_before: result.originalScore ?? result.beforeScores?.overall,
      score_after: result.optimizedScore ?? result.afterScores?.overall,
    },
    created_by: createdBy,
  };
}
